import React, { useState, useEffect, useContext, useCallback } from "react";
import { toast } from "react-toastify";
import { useHistory } from "react-router-dom";

import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Button from "@material-ui/core/Button";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import IconButton from "@material-ui/core/IconButton";
import Tooltip from "@material-ui/core/Tooltip";
import Typography from "@material-ui/core/Typography";
import Box from "@material-ui/core/Box";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import RefreshIcon from "@material-ui/icons/Refresh";
import FileCopyIcon from "@material-ui/icons/FileCopy";
import VisibilityIcon from "@material-ui/icons/Visibility";
import VisibilityOffIcon from "@material-ui/icons/VisibilityOff";
import OpenInNewIcon from "@material-ui/icons/OpenInNew";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";
import api from "../../services/api";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import HubNotificaMeDialog from "../../components/HubNotificaMeDialog";
import ConfirmationModal from "../../components/ConfirmationModal";
import toastError from "../../errors/toastError";
import { Grid } from "@material-ui/core";
import { AuthContext } from "../../context/Auth/AuthContext";
import { SocketContext } from "../../context/Socket/SocketContext";
import {
  useViewMode,
  ViewModeToggle,
  RecordsCardsGrid,
  RecordCard,
  RecordCardMeta,
} from "../../components/ViewModeToggle";

import { TbMessageChatbot, TbBrandInstagram, TbBrandFacebook } from "react-icons/tb";

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(1),
    overflowY: "scroll",
    ...theme.scrollbarStyles,
  },
  emptyState: {
    textAlign: "center",
    padding: theme.spacing(6),
    color: theme.palette.text.secondary,
  },
  tokenCell: {
    fontFamily: "monospace",
    fontSize: 13,
    maxWidth: 220,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
}));

const isSuperUser = (user) =>
  Boolean(
    user &&
      (user.super === true || user.super === 1 || String(user.super) === "1")
  );

const maskToken = (token = "") => {
  const value = String(token);
  if (value.length <= 10) return "••••••••";
  return `${value.slice(0, 6)}••••${value.slice(-4)}`;
};

const getBackendUrl = () =>
  (typeof process !== "undefined" && process.env && process.env.REACT_APP_BACKEND_URL) ||
  (typeof window !== "undefined" && window.location.origin) ||
  "";

const ChannelIcon = ({ tipo }) => {
  if (tipo === "Facebook") return <TbBrandFacebook size={28} />;
  if (tipo === "Instagram") return <TbBrandInstagram size={28} />;
  if (tipo === "Webchat") return <TbMessageChatbot size={28} />;
  return <>{tipo || "—"}</>;
};

const HubNotificaMe = () => {
  const classes = useStyles();
  const history = useHistory();
  const [loading, setLoading] = useState(true);
  const [hubChannels, setHubChannels] = useState([]);
  const [selectedHubNotificaMe, setSelectedHubNotificaMe] = useState(null);
  const [hubDialogOpen, setHubDialogOpen] = useState(false);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [deletingHubNotificaMe, setDeletingHubNotificaMe] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [reconnectingId, setReconnectingId] = useState(null);
  const [visibleTokens, setVisibleTokens] = useState({});
  const [viewMode, setViewMode] = useViewMode("hubNotificaMe");
  const { user } = useContext(AuthContext);
  const socketManager = useContext(SocketContext);

  const fetchHubNotificaMe = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/hub-notificame/list");
      setHubChannels(Array.isArray(data) ? data : data?.records || []);
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!user || user.id == null) return;
    if (!isSuperUser(user)) {
      toast.error("Esta sección está disponible solo para super administrador.");
      setTimeout(() => history.push("/"), 1000);
      return;
    }
    fetchHubNotificaMe();
  }, [user, history, fetchHubNotificaMe]);

  useEffect(() => {
    if (!user?.companyId) return undefined;
    const socket = socketManager.getSocket(user.companyId);
    const event = `company-${user.companyId}-hubnotificame`;
    const handler = (data) => {
      if (data?.action === "create" || data?.action === "update" || data?.action === "reconnect") {
        fetchHubNotificaMe();
      }
      if (data?.action === "delete") {
        setHubChannels((prev) => prev.filter((item) => Number(item.id) !== Number(data.id)));
      }
    };
    socket.on(event, handler);
    return () => {
      socket.off(event, handler);
      socket.disconnect();
    };
  }, [socketManager, user?.companyId, fetchHubNotificaMe]);

  const handleOpenHubNotificaMeDialog = () => {
    setSelectedHubNotificaMe(null);
    setHubDialogOpen(true);
  };

  const handleCloseHubNotificaMeDialog = () => {
    setSelectedHubNotificaMe(null);
    setHubDialogOpen(false);
    fetchHubNotificaMe();
  };

  const handleDeleteHubNotificaMe = async (hubId) => {
    if (!hubId || deleting) return;
    setDeleting(true);
    try {
      await api.delete(`/hub-notificame/${hubId}`);
      setHubChannels((prev) => prev.filter((item) => Number(item.id) !== Number(hubId)));
      toast.success("Token eliminado correctamente");
    } catch (err) {
      toastError(err);
    } finally {
      setDeleting(false);
      setDeletingHubNotificaMe(null);
      setConfirmModalOpen(false);
    }
  };

  const handleDeleteClick = (channel) => {
    setDeletingHubNotificaMe(channel);
    setConfirmModalOpen(true);
  };

  const handleReconnect = async (channel) => {
    if (!channel?.id || reconnectingId) return;
    setReconnectingId(channel.id);
    try {
      await api.post(`/hub-notificame/${channel.id}/reconnect`);
      toast.success("Reconexión solicitada");
      await fetchHubNotificaMe();
    } catch (err) {
      toastError(err);
    } finally {
      setReconnectingId(null);
    }
  };

  const webhookUrl = (token) => `${getBackendUrl()}/hub-webhook/${token}`;

  const copyText = async (value, successMessage) => {
    try {
      await navigator.clipboard.writeText(value);
      toast.success(successMessage);
    } catch (err) {
      toastError(err);
    }
  };

  return (
    <MainContainer>
      <ConfirmationModal
        title={
          deletingHubNotificaMe
            ? `¿Eliminar canal ${deletingHubNotificaMe.nome}?`
            : "Eliminar canal"
        }
        open={confirmModalOpen}
        onClose={() => {
          setConfirmModalOpen(false);
          setDeletingHubNotificaMe(null);
        }}
        onConfirm={() => handleDeleteHubNotificaMe(deletingHubNotificaMe?.id)}
        confirmDisabled={deleting || !deletingHubNotificaMe}
      >
        Esta acción elimina el token de Meta/NotificaMe. Los webhooks de este canal dejarán de funcionar.
      </ConfirmationModal>

      <HubNotificaMeDialog
        open={hubDialogOpen}
        onClose={handleCloseHubNotificaMeDialog}
        hubnotificameId={selectedHubNotificaMe?.id}
      />

      <MainHeader>
        <Grid container alignItems="center">
          <Grid xs={8} item>
            <Title>Conexiones Meta ({hubChannels.length})</Title>
          </Grid>
          <Grid xs={4} item>
            <Box display="flex" justifyContent="flex-end" style={{ gap: 8 }}>
              <Button
                variant="outlined"
                onClick={fetchHubNotificaMe}
                startIcon={<RefreshIcon />}
                disabled={loading}
              >
                Actualizar
              </Button>
              <Button variant="contained" onClick={handleOpenHubNotificaMeDialog} color="primary">
                Agregar
              </Button>
              <ViewModeToggle value={viewMode} onChange={setViewMode} />
            </Box>
          </Grid>
        </Grid>
      </MainHeader>

      <Paper className={classes.mainPaper} variant="outlined">
        {viewMode === "cards" ? (
          <RecordsCardsGrid>
            {hubChannels.map((channel) => (
              <RecordCard
                key={`hub-card-${channel.id}`}
                title={channel.nome}
                actions={
                  <>
                    <IconButton size="small" disabled={reconnectingId === channel.id} onClick={() => handleReconnect(channel)}>
                      <RefreshIcon />
                    </IconButton>
                    <IconButton size="small" onClick={() => handleDeleteClick(channel)}>
                      <DeleteOutlineIcon />
                    </IconButton>
                  </>
                }
              >
                <RecordCardMeta>{channel.tipo || "Canal"}</RecordCardMeta>
                <RecordCardMeta>{maskToken(channel.token)}</RecordCardMeta>
              </RecordCard>
            ))}
          </RecordsCardsGrid>
        ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell align="center">Nombre del canal</TableCell>
              <TableCell align="center">Token</TableCell>
              <TableCell align="center">Webhook</TableCell>
              <TableCell align="center">Canal</TableCell>
              <TableCell align="center">Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {hubChannels.map((channel) => {
              const shown = visibleTokens[channel.id];
              return (
                <TableRow key={channel.id}>
                  <TableCell align="center">{channel.nome}</TableCell>
                  <TableCell align="center">
                    <Box display="flex" alignItems="center" justifyContent="center" style={{ gap: 4 }}>
                      <span className={classes.tokenCell} title={shown ? channel.token : maskToken(channel.token)}>
                        {shown ? channel.token : maskToken(channel.token)}
                      </span>
                      <Tooltip title={shown ? "Ocultar token" : "Mostrar token"}>
                        <IconButton
                          size="small"
                          onClick={() =>
                            setVisibleTokens((prev) => ({ ...prev, [channel.id]: !prev[channel.id] }))
                          }
                        >
                          {shown ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Copiar token">
                        <IconButton
                          size="small"
                          onClick={() => copyText(channel.token, "Token copiado")}
                        >
                          <FileCopyIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title="Abrir webhook">
                      <IconButton
                        size="small"
                        component="a"
                        href={webhookUrl(channel.token)}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <OpenInNewIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Copiar URL del webhook">
                      <IconButton
                        size="small"
                        onClick={() => copyText(webhookUrl(channel.token), "URL del webhook copiada")}
                      >
                        <FileCopyIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title={channel.tipo || "Canal"}>
                      <span>
                        <ChannelIcon tipo={channel.tipo} />
                      </span>
                    </Tooltip>
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title="Reconectar canal">
                      <span>
                        <IconButton
                          size="small"
                          disabled={reconnectingId === channel.id}
                          onClick={() => handleReconnect(channel)}
                        >
                          <RefreshIcon />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title="Eliminar canal">
                      <IconButton size="small" onClick={() => handleDeleteClick(channel)}>
                        <DeleteOutlineIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              );
            })}
            {loading && <TableRowSkeleton columns={5} />}
            {!loading && hubChannels.length === 0 && (
              <TableRow>
                <TableCell colSpan={5}>
                  <Box className={classes.emptyState}>
                    <Typography variant="h6" gutterBottom>
                      No hay canales Meta
                    </Typography>
                    <Typography variant="body2" paragraph>
                      Agrega un token de Facebook, Instagram o WebChat para recibir y enviar mensajes por NotificaMe.
                    </Typography>
                    <Button variant="contained" color="primary" onClick={handleOpenHubNotificaMeDialog}>
                      Agregar canal
                    </Button>
                  </Box>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        )}
      </Paper>
    </MainContainer>
  );
};

export default HubNotificaMe;
